/**
 * Entry point — sets up error recovery before importing the app module graph.
 * Kept minimal: error handlers → dynamic import of bootstrap.
 */
import * as React from "react";
import { isE2EMode } from "@/lib/e2eMode";
import { isSupabaseClientEnvPlaceholder } from "@/lib/supabaseEnvPlaceholder";
import { installChunkErrorHandler, stripRefreshMarkerFromUrl } from "@/utils/serviceWorkerUpdate";

// Make React globally available before framer-motion evaluates
if (typeof window !== "undefined") {
  (window as Window & { React?: typeof React }).React = React;
  // Clean cache-bust marker immediately (before bootstrap) so address bar is www.opus9.com.
  stripRefreshMarkerFromUrl();
}
if (typeof globalThis !== "undefined") {
  (globalThis as typeof globalThis & { React?: typeof React }).React = React;
}

// ── Boot recovery ──────────────────────────────────────────────────────────
const BOOT_RECOVERY_KEY = "o9_boot_recovery_v1";

const RECOVERABLE_PATTERNS = [
  "createcontext",
  "failed to fetch dynamically imported module",
  "importing a module script failed",
  "loading chunk",
  "error loading dynamically imported module",
] as const;

function isRecoverableError(msg: string): boolean {
  const lower = msg.toLowerCase();
  return RECOVERABLE_PATTERNS.some((p) => lower.includes(p));
}

async function runBootRecovery(reason: string): Promise<void> {
  if (typeof window === "undefined") return;
  if (isE2EMode()) {
    console.warn("[OPUS9] Boot recovery skipped (E2E mode):", reason);
    return;
  }

  try {
    const prev = sessionStorage.getItem(BOOT_RECOVERY_KEY);
    if (prev && Date.now() - parseInt(prev, 10) < 30_000) return; // cooldown
    sessionStorage.setItem(BOOT_RECOVERY_KEY, Date.now().toString());
  } catch {
    return; // sessionStorage blocked — skip to avoid loops
  }

  console.warn("[OPUS9] Boot recovery:", reason);

  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
    if ("caches" in window) {
      const names = await caches.keys();
      await Promise.all(names.map((n) => caches.delete(n)));
    }
  } catch {
    // best-effort
  }

  window.location.reload();
}

// ── Global error listeners (capture phase, before module evaluation) ───────
if (typeof window !== "undefined") {
  window.addEventListener(
    "error",
    (e) => {
      if (isRecoverableError((e as ErrorEvent).message || "")) {
        void runBootRecovery(`Error: ${(e as ErrorEvent).message}`);
      }
    },
    { capture: true, passive: true },
  );

  window.addEventListener(
    "unhandledrejection",
    (e) => {
      const reason = (e as PromiseRejectionEvent).reason;
      const msg = reason instanceof Error ? reason.message : String(reason ?? "");
      if (isRecoverableError(msg)) {
        void runBootRecovery(`Rejection: ${msg}`);
      }
    },
    { capture: true, passive: true },
  );

  installChunkErrorHandler();

  // Mark script as loaded (used by index.html shell)
  window.__appScriptLoaded = true;
  // Reset the SW recovery key after the watchdog window has elapsed so future deploys can
  // self-heal again, but without allowing the watchdog to loop infinitely when a recovery
  // reload itself triggers another watchdog cycle before React has had time to mount.
  try {
    setTimeout(() => {
      try { sessionStorage.removeItem('o9_sw_recovery_v1'); } catch (_) { /* ignore */ }
    }, 15_000);
  } catch (_) { /* ignore */ }

  // Mount watchdog — if React never mounts within 18 s, recover (disabled in E2E/CI)
  if (!isE2EMode()) {
    try {
      setTimeout(() => {
        const rootMounted = Boolean(
          document.getElementById("root")?.querySelector('[data-testid="react-root"]'),
        );
        if (!rootMounted) {
          void runBootRecovery("Mount timeout");
        }
      }, 18_000);
    } catch {
      // ignore
    }
  }
}

// E2E helper: expose auth storage key; persist tab session so /dashboard/* navigations still count as E2E
const E2E_TAB_SESSION_KEY = "opus9:e2e-active";
if (typeof window !== "undefined") {
  // Detect E2E mode from URL param, mock-forms build, or (only then) localStorage mock auth.
  // Do NOT promote bare DEV + leftover __E2E_TEST_USER_ROLE__ into an E2E tab — that mis-routed
  // real demo logins (e.g. driver@opus9.com) into the admin portal after Playwright runs.
  const isE2EByUrl = window.location.search.includes("e2e=1");
  const isE2EByMockForms = import.meta.env.VITE_E2E_MOCK_FORMS === "1";
  const isE2EByStorage = (() => {
    if (!isE2EByMockForms && !isE2EByUrl) return false;
    try {
      return !!localStorage.getItem("__E2E_TEST_USER_ROLE__");
    } catch {
      return false;
    }
  })();
  if (isE2EByUrl || isE2EByMockForms || isE2EByStorage) {
    try {
      sessionStorage.setItem(E2E_TAB_SESSION_KEY, "1");
    } catch {
      /* ignore */
    }
    try {
      const envUrl = (import.meta.env.VITE_SUPABASE_URL as string | undefined) ?? "";
      const envKey =
        (import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined)
        ?? (import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined)
        ?? "";
      // Keep this in sync with the Supabase client: fixture-only builds may
      // use a loopback URL plus a placeholder key, which still resolves to the
      // placeholder client and therefore its `sb-placeholder-*` storage key.
      const isPlaceholder = isSupabaseClientEnvPlaceholder(envUrl, envKey);
      const urlForKey = isPlaceholder ? "https://placeholder.supabase.co" : envUrl;
      const ref = new URL(urlForKey).hostname.split(".")[0];
      (window as Window & { __E2E_SUPABASE_AUTH_KEY__?: string }).__E2E_SUPABASE_AUTH_KEY__ = `sb-${ref}-app-auth-token`;
    } catch {
      /* ignore */
    }
  }
}

// ── Start app ──────────────────────────────────────────────────────────────
const startApp = async (): Promise<void> => {
  try {
    const mod = await import("./bootstrap");
    mod.bootstrap();
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    console.error("[OPUS9] Bootstrap import failed:", err);
    if (isRecoverableError(msg)) {
      await runBootRecovery(`Bootstrap failed: ${msg}`);
      return;
    }

    // #app-loading sits above #root — dismiss it so the error message is visible
    try {
      const loading = document.getElementById("app-loading");
      if (loading) {
        loading.classList.add("hidden");
        setTimeout(() => loading.remove(), 300);
      }
    } catch {
      /* ignore */
    }

    const root = document.getElementById("root");
    if (root) {
      root.innerHTML = `
        <div style="display:flex;align-items:center;justify-content:center;min-height:100vh;font-family:system-ui;padding:20px;">
          <div style="text-align:center;max-width:560px;">
            <h1 style="font-size: var(--type-page-title-size);margin-bottom:12px;">Application failed to load</h1>
            <p style="margin-bottom:16px;opacity:.7;">${msg}</p>
            <button onclick="window.location.reload()" style="padding:12px 24px;background:hsl(var(--primary));color:white;border:none;border-radius:8px;font-size: var(--type-card-title-size);cursor:pointer;">Reload</button>
          </div>
        </div>
      `;
    }
  }
};

void startApp();
